import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import Text from '../../ui/Text'
import { Pool, usePoolCreateMutation, usePoolUpdateMutation } from '../../common/redux/api/web-api'
import { RootState } from '../redux/store'
import { Button } from '../../common/components/Button'
import { notifyError } from '../../common/features/notificationsManager/Notify'
import Input from '../../common/components/Input'
import useText from '../../common/hooks/useText'

export type PoolDetailsWidget = {
    save: () => void
    cancel?: () => void
    poolValue?: Pool
    poolName?: string | null
    locationCheck?: string
}

export const PoolDetailsWidget = memo(function PoolDetailsWidget({
    save,
    cancel,
    poolValue,
    poolName,
    locationCheck,
}: PoolDetailsWidget): React.ReactElement {
    const text = useText()
    const userCompanyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [addNewPool] = usePoolCreateMutation()
    const [editPool] = usePoolUpdateMutation()

    const schema = yup.object().shape({
        name: yup.string().required(text('msgNameRequired')),
    })

    const {
        register,
        handleSubmit,
        formState: { isDirty, isValid, errors },
    } = useForm<Pool>({
        mode: 'all',
        resolver: yupResolver(schema, { abortEarly: false }),
        values: { ...poolValue, name: poolName, companyId: userCompanyId },
    })

    const onSubmit = async (data: Pool) => {
        if (!poolValue && !poolName) {
            try {
                await addNewPool({
                    pool: data,
                }).unwrap()

                save()
            } catch (e) {
                notifyError(text('msgPoolNotCreated'))
            }
        }
        if (poolValue) {
            try {
                await editPool({
                    pool: { ...poolValue, name: data.name },
                }).unwrap()

                save()
            } catch (e) {
                notifyError(text('msgPoolNotUpdated'))
            }
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Input
                {...register('name')}
                id={'input-name'}
                data-testid={'name-input'}
                labelText={text('lblName')}
                autoComplete="off"
                required
                error={errors.name?.message}
            />
            <div className="modal-footer mt-6">
                {locationCheck && (
                    <Button data-testid="cancel-btn" primary={false} size="sm" onClick={() => cancel && cancel()}>
                        <Text code="btnCancel" />
                    </Button>
                )}
                <Button type="submit" data-testid="save-btn" size="sm" disabled={!isValid || !isDirty}>
                    <Text code="btnSave" />
                </Button>
            </div>
        </form>
    )
})
